<template>
    <LayoutLoggedIn :doneRendering="doneRendering" :accessType="accessType">
        <template v-slot:middle-left v-if="isPrivate()">
            <v-btn @click="toggleFilter" color="success" class="mr-2">
                <span v-if="filterForm.open">Close</span>
                <span v-else>Open</span>&nbsp;Filter
            </v-btn>
        </template>
        <h2 v-if="isPublic()">Itinerary</h2>
        <div class="car-itineraries">
            <div class="filter-wrapper" v-if="filterForm.open">
                <div>
                    <FilterForm @filter="onSubmit" @reset="onReset" :slots="['fromDate', 'toDate', 'truckNumber', 'originIatas']">
                        <template v-slot:fromDate>
                            <DatePicker id="from-date" label="From date" v-model="filterForm.fromDate"></DatePicker>
                        </template>
                        <template v-slot:toDate>
                            <DatePicker id="to-date" label="To date" v-model="filterForm.toDate"></DatePicker>
                        </template>
                        <template v-slot:truckNumber>
                            <TextInput uppercase id="truck-number" label="Truck number" v-model="filterForm.truckNumber"></TextInput>
                        </template>
                        <template v-slot:originIatas>
                            <SelectInput
                                    id="origin-iatas"
                                    label="Origin airports"
                                    v-model="filterForm.originIatas.values"
                                    :options="filterForm.originIatas.options"
                                    taggable multiple uppercase
                                    :maxLength="3"
                                    @input="updateOptions"></SelectInput>
                        </template>
                    </FilterForm>
                </div>
                <v-divider></v-divider>
            </div>

            <div class="data">
                <div>
                    <v-container>
                        <v-layout row>
                            <v-flex class="car-itinerary-not-found" v-if="hasCarItineraries && !searching">
                                <div>
                                    <Success v-if="countCarItineraries === 1">{{ countCarItineraries }} itineraries found</Success>
                                    <Success v-else-if="countCarItineraries === 100"><strong>First {{ countCarItineraries }} itineraries shown</strong>, optimize the search parameters to reduce the amount</Success>
                                    <Success v-else>{{ countCarItineraries }} itineraries found</Success>
                                </div>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex v-if="!searching" class="car-itinerary">
                                <span :class="{ 'even': index % 2 === 1, 'odd': index % 2 === 0}" :key="carItineraryHelper(carItinerary).getCarItinerary().id" v-for="(carItinerary, index) in carItineraries">
                                    <CarItineraryItem class="mb-5" :carItineraryHelper="carItineraryHelper(carItinerary)" :accessType="accessType" :encryptedData="encryptedData"></CarItineraryItem>
                                </span>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex v-if="searching" class="searching">
                                <Loading><strong>Searching for itinerary items</strong>, please wait!</Loading>
                            </v-flex>
                        </v-layout>
                        <v-layout row>
                            <v-flex class="car-itinerary-not-found" v-if="!hasCarItineraries && !searching">
                                <Info>No itinerary items found</Info>
                            </v-flex>
                        </v-layout>
                    </v-container>
                </div>
            </div>
        </div>
    </LayoutLoggedIn>

</template>

<script>
import AccessType from '@/components/Core/AccessType'
import moment from 'moment'
import LayoutLoggedIn from '@/components/Layout/LoggedIn'
import CarItineraryFilter from '@/libraries/Api/QStep/CarItineraryFilter/CarItineraryFilter'
import Parameter from '@/libraries/Api/QStep/CarItineraryFilter/Parameter'
import QStepApi from '@/libraries/Api/QStep/Api'
import CarItineraryItem from '@/components/CarItinerary/Item'
import Info from '@/components/Core/Message/Info'
import Loading from '@/components/Core/Message/Loading'
import Success from '@/components/Core/Message/Success'
import SelectInput from '@/components/Core/Input/Select'
import TextInput from '@/components/Core/Input/Text'
import DatePicker from '@/components/Core/Input/DatePicker'
import FilterForm from '@/components/Core/Filter'
import CarItineraryHelper from '@/libraries/Models/CarItinerary'

export default {
  extends: AccessType,
  components: {
    LayoutLoggedIn, CarItineraryItem, Info, Loading, Success, SelectInput, TextInput, DatePicker, FilterForm
  },
  name: 'List',
  data () {
    return {
      doneRendering: true,
      detailsVisible: false,
      carItineraries: [],
      searching: false,
      filterForm: {
        open: false,
        fromDate: new Date(),
        toDate: new Date(),
        truckNumber: this.$store.getters.truckNumber.toUpperCase(),
        cancelSource: null,
        originIatas:
            {
              options: [],
              values: []
            }
      }
    }
  },
  created () {
    if (this.isPrivate()) {
      this.loadFilterOptions()
    }
    this.loadCarItinerary()
    this.searching = true
  },
  computed: {
    countCarItineraries () {
      return this.carItineraries.length
    },
    hasCarItineraries () {
      return this.countCarItineraries > 0
    }
  },

  methods: {
    carItineraryHelper (carItinerary) {
      return new CarItineraryHelper(carItinerary)
    },
    hideFilter () {
      this.filterForm.open = false
    },
    onSubmit (e) {
      e.preventDefault()
      this.searching = true
      this.loadCarItinerary()
      this.hideFilter()
      this.storeFilterOptions()
    },
    onReset (e) {
      e.preventDefault()
      this.filterForm.fromDate = new Date()
      this.filterForm.toDate = new Date()
      this.filterForm.truckNumber = this.$store.getters.truckNumber.toUpperCase()
      this.filterForm.originIatas.values = []
      this.searching = true
      this.loadCarItinerary()
      this.hideFilter()
      this.storeFilterOptions()
    },
    loadCarItinerary () {
      let carItinerariesPromise = null
      const qStepApi = new QStepApi()

      const carItineraryFilter = new CarItineraryFilter()

      if (this.isPrivate()) {
        const columns = ['id', 'payerName', 'ccfn', 'std', 'truckNumber', 'trailerNumber', 'originIata', 'destinationIata', 'weight', 'volume', 'pickUpDate']
        columns.forEach(function (column) {
          carItineraryFilter.addColumn(column)
        })

        let originIatas = ''
        let fromDate = ''
        let toDate = ''

        if (Array.isArray(this.filterForm.originIatas.values)) {
          originIatas = this.filterForm.originIatas.values.join(',')
        }

        if (this.filterForm.fromDate !== '') {
          fromDate = moment(this.filterForm.fromDate).format('YYYY-MM-DD')
        }

        if (this.filterForm.toDate !== '') {
          toDate = moment(this.filterForm.toDate).format('YYYY-MM-DD')
        }

        carItineraryFilter.addParameter(new Parameter('fromDate', fromDate))
        carItineraryFilter.addParameter(new Parameter('toDate', toDate))
        carItineraryFilter.addParameter(new Parameter('originIatas', originIatas))
        carItineraryFilter.addParameter(new Parameter('truckNumber', this.filterForm.truckNumber))

        carItinerariesPromise = qStepApi.getCarItineraries(carItineraryFilter)
      } else if (this.isPublic()) {
        carItinerariesPromise = qStepApi.getCarItineraries(carItineraryFilter, this.encryptedData)
      }

      carItinerariesPromise.then(response => {
        if (this.lodash.get(response.data.data, 'carItineraries') !== undefined) {
          this.carItineraries = response.data.data.carItineraries
        }

        this.searching = false
      }).catch(() => {
        this.searching = false
      })
    },
    toggleFilter () {
      this.filterForm.open = !this.filterForm.open
    },
    updateOptions (searchQuery) {
      if (this.isPrivate()) {
        const qStepApi = new QStepApi()
        const _self = this

        const cancelSource = qStepApi.cancelToken()

        qStepApi.searchIata(searchQuery, cancelSource, this.cancelSource).then(response => {
          if (response.data.data !== undefined) {
            _self.filterForm.originIatas.options = response.data.data.map(data => {
              return data.code + ' - ' + data.city
            })
          }
        })

        this.cancelSource = cancelSource
      }
    },
    storeFilterOptions () {
      const data = {
        fromDate: this.filterForm.fromDate,
        toDate: this.filterForm.toDate,
        truckNumber: this.filterForm.truckNumber,
        originIatas: this.filterForm.originIatas.values
      }

      this.$store.commit('filter', data)
    },
    loadFilterOptions () {
      const filter = this.$store.getters.filter

      if (this.lodash.isDate(filter.fromDate)) {
        this.filterForm.fromDate = filter.fromDate
      }

      if (this.lodash.isDate(filter.toDate)) {
        this.filterForm.toDate = filter.toDate
      }

      if (!this.lodash.isEmpty(filter.truckNumber)) {
        this.filterForm.truckNumber = filter.truckNumber
      }

      if (!this.lodash.isEmpty(filter.originIatas)) {
        this.filterForm.originIatas.values = filter.originIatas
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
    .car-itineraries {
        .send-rfs-email {
            text-align: right;
        }

        .filter {
            max-width: 1024px;
            margin: 0 auto;
            text-align: center;

            .footer {
                vertical-align: middle;
            }

            .v-text-field__details {
                display: none;
            }
        }

        .data {
            .car-itineraries-not-found {
                margin-top: 15px;
            }
        }
    }
</style>
